// ─── BatchArrivalDialog.jsx ──────────────────────────────────────────────
//
// Scans the user's orders for container batches whose arrivalDate has reached
// today and that haven't been confirmed yet. When at least one is found, the
// dialog prompts:
//
//   "Today batch X from order Y is in stock. This stock will be reflected
//    in your API data — please confirm so the daily snapshot can absorb it."
//
// On Confirm: each pending batch is marked `arrivalConfirmed=true`. The
// daily kpi-snapshot job (already running) will then capture whatever the
// API reports going forward.
//
// On Skip: the batch stays in the pending list and the dialog re-prompts
// next time the user lands on the Dashboard.

import { useState, useMemo } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS } from "../lib/theme.js";
import { BottomSheet } from "./ui.jsx";

// Parse "DD.MM.YYYY" → Date or null
function parseDate(s) {
  if (!s || typeof s !== "string") return null;
  const parts = s.split(".");
  if (parts.length !== 3) return null;
  const [d, m, y] = parts.map(Number);
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
}

// Returns a flat list of pending batches across all orders.
// Each item: { orderId, orderNumber, batchId, batchIdx, units, arrivalDate, marketplaceIds }
export function findPendingArrivals(orders, today = new Date()) {
  const pending = [];
  const todayMs = today.getTime();
  for (const order of orders || []) {
    if (order.completed) continue;
    const batches = order.containerBatches || [];
    for (let i = 0; i < batches.length; i++) {
      const b = batches[i];
      if (b.arrivalConfirmed) continue;
      const arrival = parseDate(b.arrivalDate);
      if (!arrival) continue;
      if (arrival.getTime() > todayMs) continue;  // future — not yet
      // Estimate units in this batch
      let units = b.unitCount || 0;
      if (!units && order.unitCount) {
        const totalCt = (order.containerLines || []).reduce((s, l) => s + (l.qty || 0), 0)
          || order.totalContainers || 0;
        if (totalCt > 0 && b.containers) {
          units = Math.round((b.containers / totalCt) * order.unitCount);
        }
      }
      pending.push({
        orderId: order.id,
        orderNumber: order.number || order.id,
        supplier: order.supplier || "",
        batchId: b.id,
        batchIdx: i,
        units,
        arrivalDate: b.arrivalDate,
        marketplaceIds: order.marketplaceIds || [],
      });
    }
  }
  return pending;
}

export default function BatchArrivalDialog({ orders, orderOps, marketplaces = [] }) {
  const T = useTheme();
  const [skippedBatchIds, setSkippedBatchIds] = useState(new Set());

  const pending = useMemo(() => {
    const all = findPendingArrivals(orders, new Date());
    return all.filter(p => !skippedBatchIds.has(p.batchId));
  }, [orders, skippedBatchIds]);

  if (pending.length === 0) return null;

  const totalUnits = pending.reduce((s, p) => s + p.units, 0);

  function confirmAll() {
    // Mark every pending batch as arrivalConfirmed via orderOps.updateBatch
    // (or whatever API the orderOps exposes). Falls back to a direct update
    // through orderOps.update by patching containerBatches.
    for (const p of pending) {
      const order = orders.find(o => o.id === p.orderId);
      if (!order) continue;
      const newBatches = (order.containerBatches || []).map((b, i) =>
        i === p.batchIdx ? { ...b, arrivalConfirmed: true, arrivalConfirmedAt: new Date().toISOString() } : b
      );
      orderOps?.update?.(p.orderId, { containerBatches: newBatches });
    }
  }

  function skipBatch(batchId) {
    setSkippedBatchIds(prev => new Set([...prev, batchId]));
  }

  return (
    <BottomSheet open={true} onClose={() => setSkippedBatchIds(new Set(pending.map(p => p.batchId)))}
      title="📦 Stock Arrived — Please Confirm" maxWidth={560}>
      <div style={{ padding: "4px 0 16px" }}>
        <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, marginBottom: 14 }}>
          {pending.length === 1 ? (
            <>One supplier-order batch is scheduled to arrive today (or earlier). The arrival of this stock will be reflected in your API/inventory data — please confirm so your daily stock snapshot stays accurate.</>
          ) : (
            <><strong style={{ color: T.text }}>{pending.length}</strong> supplier-order batches are scheduled to arrive today (or earlier). Confirming tells the app to expect those units to flow through your API/inventory snapshots.</>
          )}
        </div>

        <div style={{ marginBottom: 14, padding: 12, background: T.surface, borderRadius: RADIUS.md, border: `1px solid ${T.border}` }}>
          {pending.map(p => {
            const mktNames = p.marketplaceIds
              .map(id => marketplaces.find(m => m.id === id)?.name)
              .filter(Boolean)
              .join(" · ") || "(no marketplace)";
            return (
              <div key={p.batchId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.border}22` }}>
                <span style={{ fontSize: 18 }}>📦</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                    {p.orderNumber} · batch #{p.batchIdx + 1}
                  </div>
                  <div style={{ fontSize: 11, color: T.textSub }}>
                    {p.units > 0 ? `${p.units.toLocaleString()} units` : "units unknown"} · arrived {p.arrivalDate} · {mktNames}
                  </div>
                </div>
                <button onClick={() => skipBatch(p.batchId)}
                  style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: RADIUS.sm, padding: "4px 10px", fontSize: 11, color: T.textSub, cursor: "pointer", fontFamily: FONT }}
                >Skip</button>
              </div>
            );
          })}
          <div style={{ paddingTop: 8, marginTop: 4, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
            <span style={{ color: T.textSub }}>Total units arriving:</span>
            <span style={{ fontWeight: 700, color: T.text }}>{totalUnits.toLocaleString()}</span>
          </div>
        </div>

        <button onClick={confirmAll}
          style={{ width: "100%", padding: 13, background: "linear-gradient(135deg,#34c759,#06b6d4)", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: FONT, cursor: "pointer", boxShadow: "0 8px 24px rgba(52,199,89,0.35)" }}>
          ✓ Confirm all arrivals
        </button>
        <button onClick={() => setSkippedBatchIds(new Set(pending.map(p => p.batchId)))}
          style={{ width: "100%", marginTop: 10, padding: 10, background: "transparent", border: `1px solid ${T.border}`, borderRadius: RADIUS.sm, color: T.textSub, fontFamily: FONT, fontSize: 13, cursor: "pointer" }}>
          Skip all for now
        </button>
      </div>
    </BottomSheet>
  );
}
